import { Head, Link, router, useForm } from '@inertiajs/react'
import { useState } from 'react'
import SuperAdminLayout from '../_SuperAdminLayout'
import RoleConversionModal from '../_RoleConversionModal'
import { StatusPill } from '../Dashboard'
import { sortPeople } from '../../../utils/peopleSort'

interface Tenant {
  id: number
  name: string
  slug: string
  timezone: string
  status: string
  user_count: number
  team_count: number
  meeting_count: number
  backup_count: number
  last_backup_display: string
  created_at_display: string
}

interface UserRow {
  id: number
  account_id: number
  email: string
  full_name: string
  first_name: string
  last_name: string
  title: string | null
  phone: string | null
  role: string
  status: string
  other_organizations: { id: number; name: string }[]
}
interface GuideOption { id: number; email: string; full_name: string }
interface TeamRow { id: number; name: string; member_count: number }
interface InvitationRow { id: number; email: string; role: string; status: string; expires_at_display: string; created_at_display: string }
interface EmailEvent { id: number; event_type: string; email: string; occurred_at_display: string }

interface Props {
  tenant: Tenant
  users: UserRow[]
  guides: UserRow[]
  assignable_guides: GuideOption[]
  teams: TeamRow[]
  invitations: InvitationRow[]
  recent_email_events: EmailEvent[]
}

export default function TenantShow({ tenant, users, guides, assignable_guides, teams, invitations, recent_email_events }: Props) {
  const [showDelete, setShowDelete] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')

  const inviteForm = useForm({ invitation: { email: '', role: 'admin' } })
  const sendInvite = (e: React.FormEvent) => {
    e.preventDefault()
    inviteForm.post(`/super_admin/tenants/${tenant.id}/invitations`, {
      onSuccess: () => inviteForm.reset()
    })
  }

  const createUserForm = useForm({
    user: { email: '', first_name: '', last_name: '', role: 'member', password: '' }
  })
  const submitCreateUser = (e: React.FormEvent) => {
    e.preventDefault()
    createUserForm.post(`/super_admin/tenants/${tenant.id}/users`, {
      onSuccess: () => createUserForm.reset()
    })
  }

  const addGuideForm = useForm({ email: '' })
  const [guideQuery, setGuideQuery] = useState('')
  const [guidePickerOpen, setGuidePickerOpen] = useState(false)
  const selectedGuide = assignable_guides.find(g => g.email === addGuideForm.data.email) || null
  // Task #613: sort after filtering so search results stay alphabetical.
  const filteredGuides = sortPeople(assignable_guides.filter(g => {
    const q = guideQuery.trim().toLowerCase()
    if (!q) return true
    return g.full_name.toLowerCase().includes(q) || g.email.toLowerCase().includes(q)
  }))
  const submitAddGuide = (e: React.FormEvent) => {
    e.preventDefault()
    if (!addGuideForm.data.email) return
    addGuideForm.post(`/super_admin/tenants/${tenant.id}/add_guide`, {
      onSuccess: () => {
        addGuideForm.reset()
        setGuideQuery('')
        setGuidePickerOpen(false)
      }
    })
  }
  const [removeGuideTarget, setRemoveGuideTarget] = useState<UserRow | null>(null)
  const confirmRemoveGuide = () => {
    if (!removeGuideTarget) return
    router.delete(`/super_admin/tenants/${tenant.id}/remove_guide`, {
      data: { user_id: removeGuideTarget.id },
      onFinish: () => setRemoveGuideTarget(null)
    })
  }

  const [pwUser, setPwUser] = useState<UserRow | null>(null)
  const [pwValue, setPwValue] = useState('')
  const [editUser, setEditUser] = useState<UserRow | null>(null)
  const [convertUser, setConvertUser] = useState<UserRow | null>(null)
  const submitSetPassword = () => {
    if (!pwUser) return
    router.post(
      `/super_admin/tenants/${tenant.id}/users/${pwUser.id}/set_password`,
      { password: pwValue },
      { onSuccess: () => { setPwUser(null); setPwValue('') } }
    )
  }
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
    let out = ''
    const arr = new Uint32Array(14)
    crypto.getRandomValues(arr)
    arr.forEach(n => { out += chars[n % chars.length] })
    return out
  }

  const suspend = () => router.post(`/super_admin/tenants/${tenant.id}/suspend`)
  const reactivate = () => router.post(`/super_admin/tenants/${tenant.id}/reactivate`)
  const backupNow = () => router.post(`/super_admin/tenants/${tenant.id}/backup_now`)
  const impersonate = () => router.post(`/super_admin/tenants/${tenant.id}/impersonate`)

  const submitDelete = () => {
    router.delete(`/super_admin/tenants/${tenant.id}`, {
      data: { confirmation_slug: deleteConfirm, take_safety_backup: true },
    })
  }

  const resendInvite = (id: number) => {
    router.post(`/super_admin/tenants/${tenant.id}/invitations/${id}/resend`)
  }

  return (
    <SuperAdminLayout>
      <Head title={`SuperAdmin · ${tenant.name}`} />
      <Link href="/super_admin/tenants" className="text-sm text-[#ED1C24] hover:underline">&larr; All tenants</Link>

      <div className="flex items-start justify-between mt-2 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-text-primary">{tenant.name}</h1>
            <StatusPill status={tenant.status} />
          </div>
          <div className="text-sm text-text-muted mt-1">slug <code className="bg-[#F5F5F5] px-2 py-0.5 rounded">{tenant.slug}</code> · timezone {tenant.timezone} · created {tenant.created_at_display}</div>
        </div>
        <div className="flex gap-2">
          {tenant.status === 'active' && (
            <button onClick={impersonate} className="px-3 py-2 bg-[#1B1B1B] text-white rounded-lg text-sm font-semibold hover:bg-black">
              Impersonate Company
            </button>
          )}
          <Link href={`/super_admin/tenants/${tenant.id}/edit`} className="px-3 py-2 border border-stone/30 rounded-lg text-sm hover:bg-[#F5F5F5]">Edit</Link>
          {tenant.status === 'active'
            ? <button onClick={suspend} className="px-3 py-2 border border-stone/30 rounded-lg text-sm hover:bg-[#F5F5F5]">Suspend</button>
            : <button onClick={reactivate} className="px-3 py-2 border border-stone/30 rounded-lg text-sm hover:bg-[#F5F5F5]">Reactivate</button>}
          <Link href={`/super_admin/tenants/${tenant.id}/backups`} className="px-3 py-2 border border-stone/30 rounded-lg text-sm hover:bg-[#F5F5F5]">Backups ({tenant.backup_count})</Link>
          <button onClick={backupNow} className="px-3 py-2 border border-stone/30 rounded-lg text-sm hover:bg-[#F5F5F5]">Backup now</button>
          <button onClick={() => setShowDelete(true)} className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E]">Delete tenant</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Users" value={tenant.user_count} />
        <Stat label="Teams" value={tenant.team_count} />
        <Stat label="Meetings" value={tenant.meeting_count} />
        <Stat label="Last backup" valueText={tenant.last_backup_display} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title={`Users (${users.length})`}>
          <details className="mb-3 border border-stone/20 rounded-lg">
            <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-text-primary hover:bg-[#F5F5F5]">
              + Add user directly (no email)
            </summary>
            <form onSubmit={submitCreateUser} className="p-3 space-y-2 border-t border-stone/20">
              <p className="text-xs text-text-muted">
                Use this when SendGrid is not yet configured. Creates an active
                user immediately with the password you set — share it with the
                user out-of-band.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <input
                  required
                  placeholder="First name"
                  value={createUserForm.data.user.first_name}
                  onChange={e => createUserForm.setData('user', { ...createUserForm.data.user, first_name: e.target.value })}
                  className="px-3 py-2 border border-stone/30 rounded-lg text-sm"
                />
                <input
                  required
                  placeholder="Last name"
                  value={createUserForm.data.user.last_name}
                  onChange={e => createUserForm.setData('user', { ...createUserForm.data.user, last_name: e.target.value })}
                  className="px-3 py-2 border border-stone/30 rounded-lg text-sm"
                />
              </div>
              <input
                type="email"
                required
                placeholder="user@example.com"
                value={createUserForm.data.user.email}
                onChange={e => createUserForm.setData('user', { ...createUserForm.data.user, email: e.target.value })}
                className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
              />
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={createUserForm.data.user.role}
                  onChange={e => createUserForm.setData('user', { ...createUserForm.data.user, role: e.target.value })}
                  className="px-3 py-2 border border-stone/30 rounded-lg text-sm"
                >
                  <option value="observer">Observer</option>
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="account_owner">Account Owner</option>
                  <option value="guide">Guide</option>
                </select>
                <input
                  type="text"
                  required
                  minLength={6}
                  placeholder="Password (min 6)"
                  value={createUserForm.data.user.password}
                  onChange={e => createUserForm.setData('user', { ...createUserForm.data.user, password: e.target.value })}
                  className="col-span-2 px-3 py-2 border border-stone/30 rounded-lg text-sm font-mono"
                />
              </div>
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => createUserForm.setData('user', { ...createUserForm.data.user, password: generatePassword() })}
                  className="text-xs text-[#ED1C24] hover:underline"
                >
                  Generate password
                </button>
                <button disabled={createUserForm.processing} className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E] disabled:opacity-50">
                  Create user
                </button>
              </div>
            </form>
          </details>
          {users.length === 0 ? <Empty text="No users yet." /> : (
            <ul className="divide-y divide-stone/10">
              {sortPeople(users).map(u => (
                <li key={u.id} className="py-2 text-sm flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-text-primary truncate">{u.full_name}</div>
                    <div className="text-xs text-text-muted truncate">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-text-muted">{u.role}</span>
                    <span className="text-xs text-text-muted">· {u.status}</span>
                    <button
                      onClick={() => setEditUser(u)}
                      className="text-xs px-2 py-1 border border-stone/30 rounded hover:bg-[#F5F5F5]"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => { setPwUser(u); setPwValue('') }}
                      className="text-xs px-2 py-1 border border-stone/30 rounded hover:bg-[#F5F5F5]"
                    >
                      Set password
                    </button>
                    <button
                      onClick={() => setConvertUser(u)}
                      className="text-xs px-2 py-1 border border-stone/30 rounded hover:bg-[#F5F5F5]"
                    >
                      Convert to Guide
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={`Guides (${guides.length})`}>
          <form onSubmit={submitAddGuide} className="flex items-end gap-2 mb-3">
            <div className="flex-1 relative">
              <label className="block text-xs font-medium text-text-primary mb-1">Add an existing guide</label>
              {selectedGuide ? (
                <div className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm flex items-center justify-between gap-2 bg-[#F5F5F5]">
                  <div className="min-w-0">
                    <div className="font-semibold text-text-primary truncate">{selectedGuide.full_name}</div>
                    <div className="text-xs text-text-muted truncate">{selectedGuide.email}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { addGuideForm.setData('email', ''); setGuideQuery(''); setGuidePickerOpen(true) }}
                    className="text-xs text-[#ED1C24] hover:underline shrink-0"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  value={guideQuery}
                  onChange={e => { setGuideQuery(e.target.value); setGuidePickerOpen(true) }}
                  onFocus={() => setGuidePickerOpen(true)}
                  onBlur={() => setTimeout(() => setGuidePickerOpen(false), 150)}
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
                  placeholder="Search guides by name or email…"
                />
              )}
              {!selectedGuide && guidePickerOpen && (
                <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-auto bg-white border border-stone/30 rounded-lg shadow-lg">
                  {filteredGuides.length === 0 ? (
                    <li className="px-3 py-2 text-xs text-text-muted">No matching guides.</li>
                  ) : (
                    filteredGuides.map(g => (
                      <li key={g.id}>
                        <button
                          type="button"
                          onMouseDown={e => { e.preventDefault(); addGuideForm.setData('email', g.email); setGuidePickerOpen(false) }}
                          className="w-full text-left px-3 py-2 hover:bg-[#F5F5F5]"
                        >
                          <div className="text-sm font-semibold text-text-primary truncate">{g.full_name}</div>
                          <div className="text-xs text-text-muted truncate">{g.email}</div>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
            <button disabled={addGuideForm.processing || !addGuideForm.data.email} className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E] disabled:opacity-50">
              Add guide
            </button>
          </form>
          <p className="text-xs text-text-muted mb-3">
            No password is set — guides already have a login. Guides already on this
            tenant are excluded from the list. If someone isn't a guide yet, create
            them on the{' '}
            <Link href="/super_admin/guides" className="text-[#ED1C24] hover:underline">Guides page</Link>{' '}
            first.
          </p>
          {guides.length === 0 ? <Empty text="No guides attached." /> : (
            <ul className="divide-y divide-stone/10">
              {sortPeople(guides).map(g => (
                <li key={g.id} className="py-2 text-sm flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-text-primary truncate">{g.full_name}</div>
                    <div className="text-xs text-text-muted truncate">{g.email}</div>
                  </div>
                  <button
                    onClick={() => setRemoveGuideTarget(g)}
                    className="text-xs px-2 py-1 border border-stone/30 rounded hover:bg-[#F5F5F5] shrink-0"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={`Teams (${teams.length})`}>
          {teams.length === 0 ? <Empty text="No teams." /> : (
            <ul className="divide-y divide-stone/10">
              {teams.map(t => (
                <li key={t.id} className="py-2 text-sm flex items-center justify-between">
                  <div className="font-semibold text-text-primary">{t.name}</div>
                  <span className="text-xs text-text-muted">{t.member_count} members</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={`Pending invitations (${invitations.length})`}>
          <form onSubmit={sendInvite} className="flex items-end gap-2 mb-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-text-primary mb-1">Send invite to</label>
              <input
                type="email"
                required
                value={inviteForm.data.invitation.email}
                onChange={e => inviteForm.setData('invitation', { ...inviteForm.data.invitation, email: e.target.value })}
                className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-primary mb-1">Role</label>
              <select
                value={inviteForm.data.invitation.role}
                onChange={e => inviteForm.setData('invitation', { ...inviteForm.data.invitation, role: e.target.value })}
                className="px-3 py-2 border border-stone/30 rounded-lg text-sm"
              >
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="account_owner">Account Owner</option>
              </select>
            </div>
            <button disabled={inviteForm.processing} className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E] disabled:opacity-50">
              Send
            </button>
          </form>
          {invitations.length === 0 ? <Empty text="No pending invitations." /> : (
            <ul className="divide-y divide-stone/10">
              {sortPeople(invitations).map(i => (
                <li key={i.id} className="py-2 text-sm flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-text-primary">{i.email}</div>
                    <div className="text-xs text-text-muted">{i.role} · expires {i.expires_at_display}</div>
                  </div>
                  <button onClick={() => resendInvite(i.id)} className="text-xs px-2 py-1 border border-stone/30 rounded hover:bg-[#F5F5F5]">Resend</button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={`Recent email events (${recent_email_events.length})`}>
          {recent_email_events.length === 0 ? <Empty text="No SendGrid events for this tenant yet." /> : (
            <ul className="divide-y divide-stone/10">
              {recent_email_events.map(e => (
                <li key={e.id} className="py-2 text-sm flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-text-primary">{e.email}</div>
                    <div className="text-xs text-text-muted">{e.event_type}</div>
                  </div>
                  <span className="text-xs text-text-muted">{e.occurred_at_display}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {convertUser && (
        <RoleConversionModal
          accountId={convertUser.account_id}
          accountName={convertUser.full_name}
          direction="to_guide"
          onClose={() => setConvertUser(null)}
        />
      )}

      {editUser && (
        <EditUserModal
          key={editUser.id}
          tenantId={tenant.id}
          user={editUser}
          onClose={() => setEditUser(null)}
        />
      )}

      {pwUser && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-bold text-text-primary">Set password for {pwUser.full_name}</h2>
            <p className="text-sm text-text-muted mt-2">
              {pwUser.email}
            </p>
            <p className="text-sm text-text-muted mt-2">
              The user's password will be replaced immediately. Share the new
              password with them out-of-band (chat, phone, in person) — it is
              not emailed.
            </p>
            <input
              type="text"
              value={pwValue}
              onChange={e => setPwValue(e.target.value)}
              placeholder="New password (min 6 chars)"
              className="mt-3 w-full px-3 py-2 border border-stone/30 rounded-lg text-sm font-mono"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setPwValue(generatePassword())}
              className="mt-2 text-xs text-[#ED1C24] hover:underline"
            >
              Generate password
            </button>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setPwUser(null); setPwValue('') }} className="px-3 py-2 border border-stone/30 rounded-lg text-sm">Cancel</button>
              <button
                onClick={submitSetPassword}
                disabled={pwValue.length < 6}
                className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                Set password
              </button>
            </div>
          </div>
        </div>
      )}

      {removeGuideTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-bold text-text-primary">Remove {removeGuideTarget.full_name} as a Guide?</h2>
            <p className="text-sm text-text-muted mt-2">
              This only detaches them from <strong>{tenant.name}</strong>. Their
              login and their guide membership in any other companies are left
              untouched.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRemoveGuideTarget(null)} className="px-3 py-2 border border-stone/30 rounded-lg text-sm">Cancel</button>
              <button onClick={confirmRemoveGuide} className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E]">Remove guide</button>
            </div>
          </div>
        </div>
      )}

      {showDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-bold text-text-primary">Delete tenant {tenant.name}?</h2>
            <p className="text-sm text-text-muted mt-2">This will permanently delete <strong>all data</strong> for this tenant including users, teams, meetings, rocks, metrics, topics, action items, audit logs, and backups. A safety backup is taken first.</p>
            <p className="text-sm text-text-muted mt-2">Type the slug <code className="bg-[#F5F5F5] px-1.5 py-0.5 rounded">{tenant.slug}</code> to confirm:</p>
            <input value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} className="mt-2 w-full px-3 py-2 border border-stone/30 rounded-lg text-sm" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowDelete(false)} className="px-3 py-2 border border-stone/30 rounded-lg text-sm">Cancel</button>
              <button onClick={submitDelete} disabled={deleteConfirm !== tenant.slug} className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold disabled:opacity-50">Delete forever</button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  )
}

function Stat({ label, value, valueText }: { label: string; value?: number; valueText?: string }) {
  return (
    <div className="bg-white border border-stone/20 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wide text-text-muted font-semibold">{label}</div>
      <div className="text-2xl font-bold text-text-primary mt-1">{value !== undefined ? value.toLocaleString() : valueText}</div>
    </div>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-stone/20 rounded-lg p-4">
      <h2 className="text-base font-semibold text-text-primary mb-3">{title}</h2>
      {children}
    </div>
  )
}

function Empty({ text }: { text: string }) {
  return <div className="py-4 text-center text-text-muted text-sm">{text}</div>
}

function EditUserModal({ tenantId, user, onClose }: { tenantId: number; user: UserRow; onClose: () => void }) {
  const form = useForm({
    user: {
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      title: user.title || '',
      phone: user.phone || '',
      role: user.role,
      status: user.status,
    },
  })

  const set = (key: keyof typeof form.data.user, value: string) =>
    form.setData('user', { ...form.data.user, [key]: value })

  const errors = form.errors as Record<string, string>
  const fieldErrors = [
    errors.first_name && `First name ${errors.first_name}`,
    errors.last_name && `Last name ${errors.last_name}`,
    errors.phone && `Phone ${errors.phone}`,
    errors.role,
    errors.status,
    errors.base,
  ].filter(Boolean)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    form.transform(data => ({
      user: { ...data.user, email: data.user.email.trim() },
    }))
    form.patch(`/super_admin/tenants/${tenantId}/users/${user.id}`, {
      onSuccess: () => onClose(),
    })
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={submit} className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-text-primary">Edit {user.full_name}</h2>

        {fieldErrors.length > 0 && (
          <ul className="mt-3 list-disc list-inside text-xs text-[#ED1C24] space-y-0.5">
            {fieldErrors.map((msg, i) => <li key={i}>{msg}</li>)}
          </ul>
        )}

        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
            Shared across all companies
          </div>
          <p className="text-xs text-text-muted mb-3">
            These fields live on the person's shared login and change everywhere they belong.
          </p>
          {user.other_organizations.length > 0 && (
            <div className="mb-3 rounded-lg border border-[#F5A623]/40 bg-[#FFF8EC] p-3">
              <p className="text-xs font-semibold text-[#8A5A00]">
                Heads up: this login also belongs to {user.other_organizations.length} other{' '}
                {user.other_organizations.length === 1 ? 'company' : 'companies'}. Changes to the
                shared fields below will be seen there too:
              </p>
              <ul className="mt-2 list-disc list-inside text-xs text-text-muted space-y-0.5">
                {user.other_organizations.map(org => (
                  <li key={org.id}>{org.name}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-text-primary mb-1">First name</label>
              <input
                required
                value={form.data.user.first_name}
                onChange={e => set('first_name', e.target.value)}
                className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-primary mb-1">Last name</label>
              <input
                required
                value={form.data.user.last_name}
                onChange={e => set('last_name', e.target.value)}
                className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
              />
            </div>
          </div>
          <div className="mt-2">
            <label className="block text-xs font-medium text-text-primary mb-1">Email</label>
            <input
              type="email"
              required
              value={form.data.user.email}
              onChange={e => set('email', e.target.value)}
              className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
            />
            {errors.email && (
              <p className="text-xs text-[#ED1C24] mt-1">{errors.email}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <label className="block text-xs font-medium text-text-primary mb-1">Title</label>
              <input
                value={form.data.user.title}
                onChange={e => set('title', e.target.value)}
                className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-primary mb-1">Phone</label>
              <input
                value={form.data.user.phone}
                onChange={e => set('phone', e.target.value)}
                className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
              />
            </div>
          </div>
        </div>

        <div className="mt-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
            This company only
          </div>
          <p className="text-xs text-text-muted mb-3">
            These apply only to this tenant and don't affect the person's other companies.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-text-primary mb-1">Role</label>
              <select
                value={form.data.user.role}
                onChange={e => set('role', e.target.value)}
                className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
              >
                <option value="observer">Observer</option>
                <option value="member">Member</option>
                <option value="admin">Admin</option>
                <option value="account_owner">Account Owner</option>
                <option value="guide">Guide</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-primary mb-1">Status</label>
              <select
                value={form.data.user.status}
                onChange={e => set('status', e.target.value)}
                className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm"
              >
                <option value="active">Active</option>
                <option value="deactivated">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-3 py-2 border border-stone/30 rounded-lg text-sm">Cancel</button>
          <button
            type="submit"
            disabled={form.processing}
            className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            Save changes
          </button>
        </div>
      </form>
    </div>
  )
}
